import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "./api";

const HULL_SIZE_BY_TYPE = {
  eight: "8",
  four: "4",
  pair: "2",
};
const todayYMD = () => new Date().toISOString().slice(0, 10);
const makeEmptyBoat = () => ({ type: "eight", name: "", hull: "", coach: "", oars: "", coxBox: "", speedCoach: "" });

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // shell template form
  const [shellName, setShellName] = useState("");
  const [boats, setBoats] = useState([makeEmptyBoat()]);
  const [editingShellId, setEditingShellId] = useState(null);
  const [boatConfig, setBoatConfig] = useState({ hulls: [], oars: [], coxBoxes: [], launches: [], speedCoaches: [], coaches: [] });
  const [showForm, setShowForm] = useState(false);

  const hullOptionsForType = useCallback((boatType, currentValue) => {
    const size = HULL_SIZE_BY_TYPE[boatType] || null;
    const filtered = boatConfig.hulls.filter((h) => !h.size || !size || h.size === size);
    if (currentValue && !filtered.some((h) => h.value === currentValue)) {
      filtered.push({ id: `custom-${currentValue}`, value: currentValue, size: null });
    }
    return filtered;
  }, [boatConfig.hulls]);

  const coachOptions = useCallback((currentValue) => {
    const base = Array.isArray(boatConfig.coaches) ? boatConfig.coaches : [];
    if (currentValue && !base.includes(currentValue)) {
      return [...base, currentValue];
    }
    return base;
  }, [boatConfig.coaches]);

  const oarOptions = useCallback((currentValue) => {
    const base = Array.isArray(boatConfig.oars) ? boatConfig.oars : [];
    if (currentValue && !base.some((o) => o.value === currentValue)) {
      return [...base, { id: `custom-${currentValue}`, value: currentValue }];
    }
    return base;
  }, [boatConfig.oars]);

  const coxBoxOptions = useCallback((currentValue) => {
    const base = Array.isArray(boatConfig.coxBoxes) ? boatConfig.coxBoxes : [];
    if (currentValue && !base.some((c) => c.value === currentValue)) {
      return [...base, { id: `custom-${currentValue}`, value: currentValue }];
    }
    return base;
  }, [boatConfig.coxBoxes]);
  const speedCoachOptions = useCallback((currentValue) => {
    const base = Array.isArray(boatConfig.speedCoaches) ? boatConfig.speedCoaches : [];
    if (currentValue && !base.some((s) => s.value === currentValue)) {
      return [...base, { id: `custom-${currentValue}`, value: currentValue }];
    }
    return base;
  }, [boatConfig.speedCoaches]);

  async function load() {
    try {
      setLoading(true);
      setErr("");
      const list = await apiFetch(`/lineup-templates`);
      const normalized = Array.isArray(list)
        ? list.map((t) => {
            let payload = [];
            try {
              const raw = t.payload;
              payload = Array.isArray(raw) ? raw : JSON.parse(raw ?? "[]");
            } catch {
              payload = [];
            }
            const isShell = !t.sourceDate;
            const displaySourceDate = isShell ? todayYMD() : t.sourceDate;
            const boatCount = typeof t.boatCount === "number" ? t.boatCount : Array.isArray(payload) ? payload.length : 0;
            return {
              ...t,
              payload,
              isShell,
              displaySourceDate,
              boatCount,
            };
          })
        : [];
      setTemplates(normalized);
    } catch (e) {
      setErr(e?.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    async function loadBoatConfig() {
      try {
        const config = await apiFetch(`/boat-config`);
        setBoatConfig({
          hulls: Array.isArray(config?.assets?.hull) ? config.assets.hull : (Array.isArray(config?.hulls) ? config.hulls : []),
          oars: Array.isArray(config?.assets?.oars) ? config.assets.oars : (Array.isArray(config?.oars) ? config.oars : []),
          coxBoxes: Array.isArray(config?.assets?.cox_boxes) ? config.assets.cox_boxes : (Array.isArray(config?.coxBoxes) ? config.coxBoxes : []),
          launches: Array.isArray(config?.assets?.launch) ? config.assets.launch : (Array.isArray(config?.launches) ? config.launches : []),
          speedCoaches: Array.isArray(config?.assets?.speed_coach) ? config.assets.speed_coach : (Array.isArray(config?.speedCoaches) ? config.speedCoaches : []),
          coaches: Array.isArray(config?.coaches) ? config.coaches : [],
        });
      } catch (e) {
        console.error("Failed to load boat config", e);
      }
    }
    loadBoatConfig();
  }, []);

  function resetShellForm() {
    setShellName("");
    setBoats([makeEmptyBoat()]);
    setEditingShellId(null);
    setShowForm(false);
  }

  async function saveShellTemplate(e) {
    e.preventDefault();
    if (!shellName.trim()) {
      alert("Please enter a template name");
      return;
    }
    if (boats.length === 0) {
      alert("Please add at least one boat");
      return;
    }
    try {
      if (editingShellId) {
        await apiFetch(`/lineup-templates/${editingShellId}/shell`, {
          method: "PUT",
          body: JSON.stringify({ name: shellName.trim(), boats }),
        });
        alert("Shell template updated!");
      } else {
        await apiFetch(`/lineup-templates/shell`, {
          method: "POST",
          body: JSON.stringify({ name: shellName.trim(), boats }),
        });
        alert("Shell template saved!");
      }
      resetShellForm();
      await load();
    } catch (e) {
      alert(e?.message || (editingShellId ? "Failed to update shell template" : "Failed to save shell template"));
    }
  }

  function addBoat() {
    setBoats([...boats, makeEmptyBoat()]);
  }

  function removeBoat(index) {
    if (boats.length === 1) {
      alert("Must have at least one boat");
      return;
    }
    setBoats(boats.filter((_, i) => i !== index));
  }

  function updateBoat(index, field, value) {
    setBoats(prev => {
      const updated = [...prev];
      const next = { ...updated[index], [field]: value };
      if (field === "type") {
        const allowed = hullOptionsForType(value, next.hull).map(h => h.value);
        if (next.hull && !allowed.includes(next.hull)) {
          next.hull = "";
        }
      }
      updated[index] = next;
      return updated;
    });
  }

  async function deleteTemplate(id) {
    if (!window.confirm("Delete this template?")) return;
    try {
      await apiFetch(`/lineup-templates/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert(e?.message || "Failed to delete template");
    }
  }

  async function startEditShell(template) {
    try {
      const detail = await apiFetch(`/lineup-templates/${template.id}`);
      const payload = Array.isArray(detail?.payload) ? detail.payload : [];
      setEditingShellId(detail.id);
      setShellName(detail.name || "");
      const parsedBoats = payload.length
        ? payload.map((boat) => ({
            type: ["eight", "four", "pair"].includes(boat.type) ? boat.type : "eight",
            name: boat.name || "",
            hull: boat.hull || "",
            coach: boat.coach || "",
            oars: boat.oars || "",
            coxBox: boat.coxBox || "",
            speedCoach: boat.speedCoach || "",
          }))
        : [makeEmptyBoat()];
      setBoats(parsedBoats);
      setShowForm(true);
    } catch (e) {
      console.error("Failed to load template detail", e);
      alert(e?.message || "Failed to load template for editing");
    }
  }

  function cancelShellEdit() {
    resetShellForm();
  }

  const isEditingShell = Boolean(editingShellId);

  return (
    <div className="card boat-config-card-compact">
      <div className="boat-config-header-compact">
        <div>
          <h2 className="h2" style={{ marginTop: 0, marginBottom: 4 }}>Lineup Templates</h2>
          <p className="help" style={{ margin: 0 }}>
            Save and manage lineup templates for quick boat setup
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {loading && <span className="help">Loading…</span>}
          {err && <span className="help boat-config-error">{err}</span>}
          {!showForm && (
            <button
              className="btn small"
              onClick={() => setShowForm(true)}
            >
              + New Template
            </button>
          )}
        </div>
      </div>

      {/* Create/Edit Form - Collapsible */}
      {showForm && (
        <form onSubmit={saveShellTemplate}>
          <div className="boat-config-form-compact" style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 12 }}>
            <input
              className="input"
              placeholder="Template name (e.g., Standard 4x 8s Practice)"
              value={shellName}
              onChange={(e) => setShellName(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <button
              type="submit"
              className="btn small"
              disabled={!shellName.trim() || boats.length === 0}
              style={{ whiteSpace: 'nowrap' }}
            >
              {isEditingShell ? "Update" : "Save"}
            </button>
            <button
              type="button"
              className="btn ghost small"
              onClick={cancelShellEdit}
            >
              Cancel
            </button>
          </div>

          <div style={{ padding: '12px 0' }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '0 12px' }}>
              <strong style={{ fontSize: 13 }}>Boats ({boats.length})</strong>
              <button type="button" className="btn ghost tiny" onClick={addBoat}>+ Add Boat</button>
            </div>

            <ul className="list" style={{ margin: 0 }}>
              {boats.map((boat, index) => (
                <li key={index} className="boat-config-item-compact" style={{ display: 'block', padding: '8px 12px' }}>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ fontSize: 13 }}>Boat {index + 1}</strong>
                    {boats.length > 1 && (
                      <button
                        type="button"
                        className="btn-icon danger"
                        onClick={() => removeBoat(index)}
                        title="Remove boat"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    <select
                      className="input-compact"
                      value={boat.type}
                      onChange={(e) => updateBoat(index, "type", e.target.value)}
                      style={{ minWidth: 90 }}
                    >
                      <option value="eight">Eight</option>
                      <option value="four">Four</option>
                      <option value="pair">Pair</option>
                    </select>

                    <select
                      className="input-compact"
                      value={boat.hull}
                      onChange={(e) => updateBoat(index, "hull", e.target.value)}
                      style={{ minWidth: 110, flex: 1 }}
                    >
                      <option value="">Hull…</option>
                      {hullOptionsForType(boat.type, boat.hull).map((hull) => (
                        <option key={hull.id ?? hull.value} value={hull.value}>
                          {hull.value}
                        </option>
                      ))}
                    </select>

                    <select
                      className="input-compact"
                      value={boat.coach}
                      onChange={(e) => updateBoat(index, "coach", e.target.value)}
                      style={{ minWidth: 100, flex: 1 }}
                    >
                      <option value="">Coach…</option>
                      {coachOptions(boat.coach).map((coachName) => (
                        <option key={coachName} value={coachName}>
                          {coachName}
                        </option>
                      ))}
                    </select>

                    <select
                      className="input-compact"
                      value={boat.oars}
                      onChange={(e) => updateBoat(index, "oars", e.target.value)}
                      style={{ minWidth: 100, flex: 1 }}
                    >
                      <option value="">Oars…</option>
                      {oarOptions(boat.oars).map((oarsOption) => (
                        <option key={oarsOption.id ?? oarsOption.value} value={oarsOption.value}>
                          {oarsOption.value}
                        </option>
                      ))}
                    </select>

                    <select
                      className="input-compact"
                      value={boat.coxBox}
                      onChange={(e) => updateBoat(index, "coxBox", e.target.value)}
                      style={{ minWidth: 100, flex: 1 }}
                    >
                      <option value="">Cox Box…</option>
                      {coxBoxOptions(boat.coxBox).map((coxBoxOption) => (
                        <option key={coxBoxOption.id ?? coxBoxOption.value} value={coxBoxOption.value}>
                          {coxBoxOption.value}
                        </option>
                      ))}
                    </select>

                    <select
                      className="input-compact"
                      value={boat.speedCoach}
                      onChange={(e) => updateBoat(index, "speedCoach", e.target.value)}
                      style={{ minWidth: 120, flex: 1 }}
                    >
                      <option value="">Speed Coach…</option>
                      {speedCoachOptions(boat.speedCoach).map((speedCoachOption) => (
                        <option key={speedCoachOption.id ?? speedCoachOption.value} value={speedCoachOption.value}>
                          {speedCoachOption.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </form>
      )}

      {/* Templates List */}
      <div className="boat-config-tab-content">
        {templates.length === 0 ? (
          <p className="help" style={{ textAlign: 'center', padding: 16 }}>
            No templates yet. Create one above!
          </p>
        ) : (
          <ul className="list" style={{ margin: 0 }}>
            {templates.map(t => (
              <li key={t.id} className="boat-config-item-compact">
                <div className="boat-config-item-info">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <strong>{t.name}</strong>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                      {t.isShell && (
                        <span className="boat-config-size-badge" style={{ background: 'rgba(79,140,249,0.18)', color: '#93c5fd' }}>
                          Shell
                        </span>
                      )}
                      {typeof t.boatCount === "number" && (
                        <span className="help">{t.boatCount} {t.boatCount === 1 ? 'boat' : 'boats'}</span>
                      )}
                      {t.displaySourceDate && !t.isShell && (
                        <span className="help">• {new Date(t.displaySourceDate).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="boat-config-item-actions">
                  {t.isShell && (
                    <button
                      className="btn ghost tiny"
                      onClick={() => startEditShell(t)}
                      title="Edit template"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => deleteTemplate(t.id)}
                    title="Delete template"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
